<script lang="ts">
  import { onDestroy, onMount } from 'svelte'
  import { tStore } from '../../i18n'
  import {
    retrievalsApi,
    type RetrievalFilters,
    type RetrievalRun,
    type RetrievalStatus,
    type RetrievalSummary,
  } from '../../api/retrievals'
  import { sourcesApi, type Source } from '../../api/sources'
  import PageHeader from '../../components/PageHeader.svelte'
  import StatusBadge from '../../components/StatusBadge.svelte'
  import Dropdown from '../../components/Dropdown.svelte'
  import ProcurementPager from './dashboard/ProcurementPager.svelte'
  import { formatDateTime, formatDurationMs } from '../../composables/format'
  import { useAbortable } from '../../composables/useAbortable'
  import { buildSourceLabels, resolveSourceLabel } from '../../composables/sourceNames'
  import { formatRunError } from '../../composables/runErrorMessage'

  // While at least one run is still "running", poll at this interval so the
  // page catches its completion quickly. Otherwise fall back to a slow
  // heartbeat so a run that starts while the page is idle still shows up
  // without a manual refresh.
  const FAST_REFRESH_INTERVAL_MS = 5000
  const HEARTBEAT_INTERVAL_MS = 30000

  const PAGE_SIZES = [10, 25, 50, 100, 200] as const
  const DEFAULT_PAGE_SIZE = 25

  let runs = $state<RetrievalRun[]>([])
  let summary = $state<RetrievalSummary | null>(null)
  let sources = $state<Source[]>([])
  let loading = $state(true)
  let errorMsg = $state<string | null>(null)
  let filters = $state<RetrievalFilters>({})
  // Filters actually applied to the loaded data (confirmed via the Filter
  // button / Refresh). `filters` itself changes as soon as the user touches a
  // dropdown, before confirming, so the background refresh must not pick that
  // up mid-edit.
  let appliedFilters = $state<RetrievalFilters>({})

  let pageSize = $state(DEFAULT_PAGE_SIZE)
  let offset = $state(0)

  // hasRunning only looks at the loaded page. The newest runs sit on page 1,
  // which is where a running run is expected to be, so the fast poll still
  // catches it without a count of running runs in the summary.
  let hasRunning = $derived(runs.some((r) => r.status === 'running'))

  let pageAnchor = $state<HTMLElement | undefined>(undefined)

  const { start } = useAbortable()

  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  let refreshController: AbortController | null = null
  let refreshInFlight = false

  onMount(() => {
    const signal = start()
    // Schedule only after the initial load resolves, so the very first
    // background poll already sees whether a loaded run is running (and
    // picks the fast interval) instead of reading the still-empty `runs`.
    void Promise.all([loadSources(signal), load(signal)]).then(() => {
      if (!signal.aborted) scheduleNextRefresh()
    })
  })

  onDestroy(() => {
    if (refreshTimer !== null) clearTimeout(refreshTimer)
    refreshController?.abort()
  })

  // Opens a fresh request scope (cancelling any in-flight load) and reloads the
  // current page. Keeps `offset` as-is, so the refresh button stays on the page
  // the user is looking at. Callers that change the query (filters, page size)
  // reset `offset` to 0 before calling this.
  function reload(): void {
    void load(start())
  }

  async function loadSources(signal: AbortSignal) {
    try {
      sources = await sourcesApi.list({ signal })
    } catch {
      // tolerate missing sources
    }
  }

  // The page of runs and the summary are fetched together so the KPI cards and
  // the pager always describe the same filtered set.
  function fetchPage(signal: AbortSignal): Promise<[RetrievalRun[], RetrievalSummary]> {
    return Promise.all([
      retrievalsApi.list({ ...appliedFilters, limit: pageSize, offset }, { signal }),
      retrievalsApi.summary(appliedFilters, { signal }),
    ])
  }

  async function load(signal: AbortSignal) {
    loading = true
    errorMsg = null
    try {
      const [page, totals] = await fetchPage(signal)
      if (signal.aborted) return
      runs = page
      summary = totals
    } catch (err) {
      if (signal.aborted) return // cancelled by a newer load or teardown
      errorMsg = err instanceof Error ? err.message : String(err)
    } finally {
      if (!signal.aborted) loading = false
    }
  }

  function applyFilters() {
    appliedFilters = { ...filters }
    offset = 0
    reload()
  }

  function clearFilters() {
    filters = {}
    applyFilters()
  }

  function changePageSize(next: number) {
    if (next === pageSize) return
    pageSize = next
    offset = 0
    reload()
  }

  function prevPage() {
    if (offset === 0) return
    offset = Math.max(0, offset - pageSize)
    reload()
    scrollToTop()
  }

  function nextPage() {
    if (!hasMore) return
    offset = offset + pageSize
    reload()
    scrollToTop()
  }

  function goToPage(page: number) {
    offset = (page - 1) * pageSize
    reload()
    scrollToTop()
  }

  // Scrolls to the top of the whole page, not the top of the table as the
  // dashboard does. Here the title, KPI cards and filters are context the user
  // still wants in view after paging, so the anchor is the page itself.
  function scrollToTop() {
    pageAnchor?.scrollIntoView({ block: 'start' })
  }

  function scheduleNextRefresh(): void {
    const delay = hasRunning ? FAST_REFRESH_INTERVAL_MS : HEARTBEAT_INTERVAL_MS
    refreshTimer = setTimeout(() => {
      void refreshSilently().finally(scheduleNextRefresh)
    }, delay)
  }

  // Background refresh: never flips `loading`, so the table stays mounted and
  // updates in place (the keyed {#each} below patches existing rows) instead
  // of flashing to the "Loading…" state. Errors are swallowed — they'll
  // surface on the next explicit load.
  async function refreshSilently(): Promise<void> {
    if (loading || refreshInFlight) return
    refreshController?.abort()
    const controller = new AbortController()
    refreshController = controller
    refreshInFlight = true
    try {
      const [page, totals] = await fetchPage(controller.signal)
      if (controller.signal.aborted) return
      runs = page
      summary = totals
    } catch {
      // Background refresh is best-effort; errors surface on the next explicit reload.
    } finally {
      if (refreshController === controller) refreshController = null
      if (!controller.signal.aborted) refreshInFlight = false
    }
  }

  function tone(s: RetrievalStatus) {
    if (s === 'success') return 'success' as const
    if (s === 'failed') return 'danger' as const
    return 'info' as const
  }

  // KPI numbers come from the server-side summary over the whole filtered set,
  // so they stay the same whichever page is shown.
  let totalRuns = $derived(summary?.total ?? 0)
  let totalFailed = $derived(summary?.totalFailed ?? 0)
  let totalNew = $derived(summary?.totalNew ?? 0)

  let totalCount = $derived(summary?.total ?? null)
  let currentPage = $derived(Math.floor(offset / pageSize) + 1)
  let totalPages = $derived(totalCount !== null ? Math.ceil(totalCount / pageSize) : null)
  let rangeFrom = $derived(runs.length === 0 ? 0 : offset + 1)
  let rangeTo = $derived(offset + runs.length)
  let hasMore = $derived(totalCount !== null && offset + runs.length < totalCount)

  const statusOptions: RetrievalStatus[] = ['running', 'success', 'failed']

  const sourceOptions = $derived([
    { value: undefined, label: $tStore('common.all') },
    ...sources.map((src) => ({ value: src.id, label: src.displayName })),
  ])
  const sourceLabels = $derived(buildSourceLabels(sources))
  const statusDropdownOptions = $derived([
    { value: undefined, label: $tStore('common.all') },
    ...statusOptions.map((opt) => ({
      value: opt,
      label: $tStore(`views.ingestionRuns.status.${opt}`),
    })),
  ])
</script>

<section class="runs" bind:this={pageAnchor}>
  <PageHeader
    title={$tStore('views.ingestionRuns.title')}
    subtitle={$tStore('views.ingestionRuns.subtitle')}
  >
    {#snippet actions()}
      <button class="btn btn--secondary" onclick={reload} disabled={loading}>
        {$tStore('common.refresh')}
      </button>
    {/snippet}
  </PageHeader>

  <div class="summary">
    <div class="card kpi">
      <span class="kpi__label">{$tStore('views.ingestionRuns.columns.id')}</span>
      <span class="kpi__value">{totalRuns}</span>
    </div>
    <div class="card kpi">
      <span class="kpi__label">{$tStore('views.ingestionRuns.columns.new')}</span>
      <span class="kpi__value">{totalNew}</span>
    </div>
    <div class="card kpi">
      <span class="kpi__label">{$tStore('views.ingestionRuns.status.failed')}</span>
      <span class="kpi__value" class:bad={totalFailed > 0}>{totalFailed}</span>
    </div>
  </div>

  <form
    class="card filters"
    onsubmit={(e) => {
      e.preventDefault()
      applyFilters()
    }}
  >
    <label class="field">
      <span class="field__label">{$tStore('views.ingestionRuns.filters.source')}</span>
      <Dropdown bind:value={filters.sourceId} options={sourceOptions} />
    </label>

    <label class="field">
      <span class="field__label">{$tStore('views.ingestionRuns.filters.status')}</span>
      <Dropdown bind:value={filters.status} options={statusDropdownOptions} />
    </label>

    <div class="filters__actions">
      <button type="submit" class="btn btn--primary">{$tStore('common.filter')}</button>
      <button type="button" class="btn btn--secondary" onclick={clearFilters}>
        {$tStore('common.clearFilters')}
      </button>
    </div>
  </form>

  {#if loading}
    <p class="muted">{$tStore('views.ingestionRuns.loading')}</p>
  {:else if errorMsg}
    <p class="error">{errorMsg}</p>
  {:else if runs.length === 0}
    <p class="muted">{$tStore('views.ingestionRuns.empty')}</p>
  {:else}
    <div class="card table-wrap">
      <table class="table">
        <thead>
          <tr>
            <th>{$tStore('views.ingestionRuns.columns.id')}</th>
            <th>{$tStore('views.ingestionRuns.columns.source')}</th>
            <th>{$tStore('views.ingestionRuns.columns.startedAt')}</th>
            <th>{$tStore('views.ingestionRuns.columns.duration')}</th>
            <th>{$tStore('views.ingestionRuns.columns.status')}</th>
            <th>
              <abbr class="metric-help" title={$tStore('views.ingestionRuns.columnHelp.found')}>
                {$tStore('views.ingestionRuns.columns.found')}
              </abbr>
            </th>
            <th>
              <abbr class="metric-help" title={$tStore('views.ingestionRuns.columnHelp.new')}>
                {$tStore('views.ingestionRuns.columns.new')}
              </abbr>
            </th>
            <th>
              <abbr class="metric-help" title={$tStore('views.ingestionRuns.columnHelp.updated')}>
                {$tStore('views.ingestionRuns.columns.updated')}
              </abbr>
            </th>
            <th>{$tStore('views.ingestionRuns.columns.error')}</th>
          </tr>
        </thead>
        <tbody>
          {#each runs as r (r.id)}
            {@const runError = formatRunError(r.error, $tStore, sourceLabels)}
            <tr>
              <td class="mono">#{r.id}</td>
              <td>{resolveSourceLabel(sourceLabels, r.sourceName)}</td>
              <td>{formatDateTime(r.startedAt)}</td>
              <td>{formatDurationMs(r.startedAt, r.completedAt)}</td>
              <td>
                <StatusBadge
                  label={$tStore(`views.ingestionRuns.status.${r.status}`)}
                  tone={tone(r.status)}
                />
              </td>
              <td>{r.procurementsFound}</td>
              <td>{r.procurementsNew}</td>
              <td>{r.procurementsUpdated}</td>
              <td class="error-cell" title={runError?.title}>{runError?.text ?? ''}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <ProcurementPager
      {pageSize}
      {offset}
      {rangeFrom}
      {rangeTo}
      {currentPage}
      {totalPages}
      {totalCount}
      {hasMore}
      {loading}
      pageSizes={PAGE_SIZES}
      onPageSizeChange={changePageSize}
      onPrev={prevPage}
      onNext={nextPage}
      onGoToPage={goToPage}
    />
  {/if}
</section>

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .runs {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .summary {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: v.$space-md;

    @media (max-width: v.$bp-sm) {
      grid-template-columns: 1fr;
    }
  }

  .kpi {
    display: flex;
    flex-direction: column;
    gap: v.$space-xs;

    &__label {
      color: v.$color-text-muted;
      font-size: 0.875rem;
    }

    &__value {
      font-size: 1.75rem;
      font-weight: 600;

      &.bad {
        color: v.$color-danger-fg;
      }
    }
  }

  .filters {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: v.$space-md;
    align-items: end;

    @media (max-width: v.$bp-sm) {
      grid-template-columns: 1fr;
    }
  }

  .filters__actions {
    display: flex;
    gap: v.$space-sm;
    justify-content: flex-end;
  }

  .table-wrap {
    overflow-x: auto;
    padding: 0;
  }

  .error-cell {
    color: v.$color-danger-fg;
    font-size: 0.8125rem;
    max-width: 18rem;
    overflow-wrap: break-word;
  }

  .metric-help {
    cursor: help;
    text-decoration: underline dotted;
    text-underline-offset: 0.18em;
  }
</style>
